// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import { addToChainAfter, addToChainBefore, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { addWishListLines } from "@insite/client-framework/Services/WishListService";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import {
    AddLineToWishList,
    AddToWishListParameter,
    AddWishList,
    chain,
    ExecuteOnSuccessCallback,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import { xCommonUpdate } from "@plumberssupply/App/Handlers/XNG/Common";
import { xngAddWishList } from "@plumberssupply/App/Services/WishListService";

export interface XngAddToWishListParameter extends AddToWishListParameter {
    plumbers_jobName?: string;
    plumbers_isSaved?: boolean;
    plumbers_isSingleUse?: boolean;
    clearCart?: boolean;
}

interface Props {
    result: {
        wishList?: WishListModel;
        errorMessage?: string;
    };
}

type HandlerType = Handler<XngAddToWishListParameter, Props>;

export const XngAddWishList: HandlerType = async props => {
    props.result = {};
    try {
        props.result.wishList =
            props.parameter.selectedWishList ??
            (await xngAddWishList({
                name: props.parameter.newListName,
                // XNG-Changes
                plumbers_jobName: props.parameter.plumbers_jobName,
                plumbers_isSaved: props.parameter.plumbers_isSaved,
                plumbers_isSingleUse: props.parameter.plumbers_isSingleUse,
            }));
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.result.errorMessage = error.errorJson.message;
            return;
        }
        throw error;
    }
};

export const XNGAddLineToWishList: HandlerType = async props => {
    if (!props.result.wishList) {
        return;
    }

    try {
        const lines = props.parameter.productInfos.map(productInfo => ({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
            properties: (productInfo as any).properties,
        }));
        await addWishListLines({ wishList: props.result.wishList, lines });
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.result.errorMessage = error.errorJson.message;
            return;
        }
        if (error?.status === 404) {
            props.result.errorMessage = error.errorMessage;
            return;
        }
        throw error;
    }
};

export const DispatchRemoveAllCartLines: HandlerType = props => {
    if (!props.parameter.clearCart) {
        return;
    }
    props.dispatch(xCommonUpdate({ key: "addToListClearCart", value: false }));
    props.dispatch(clearCurrentCart());
};

replaceInChain(chain, AddWishList, XngAddWishList);
replaceInChain(chain, AddLineToWishList, XNGAddLineToWishList);

addToChainBefore(chain, ExecuteOnSuccessCallback, DispatchRemoveAllCartLines);
